import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, message, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { CommonDrawer, PageHeaderActions } from '@/components';
import { PencilIcon } from '@/assets/icons/icons';
import { useToken, getPermissions } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import { useTableScroll } from '@/hooks/useTableScroll';
import type { RootState } from '@/redux-toolkit/store';
import {
  FreightMarkupRateCarrierOption,
  FreightMarkupRateData,
  FreightMarkupRateTeamOption,
  GetFreightMarkupRateOptions,
  GetFreightMarkupRates,
} from '@/apis/freightMarkupRates';
import FreightMarkupRateForm, {
  ALL_SERVICE_LEVELS,
  SEED_MARKUP_PERCENT,
  MarkupTeamRow,
} from './FreightMarkupRateForm';
import './FreightMarkupRates.css';

type CourierBucket = 'FedEx' | 'UPS';

function courierBucket(name: string | undefined): CourierBucket | null {
  const normalized = (name || '').trim().toLowerCase();
  if (normalized === 'fedex') return 'FedEx';
  if (normalized === 'ups') return 'UPS';
  return null;
}

type ServiceLevelLine = {
  key: string;
  label: string;
  markup: string;
  tooltip: string;
};

type TeamMarkupRow = MarkupTeamRow & {
  fedex: ServiceLevelLine[];
  ups: ServiceLevelLine[];
};

function ServiceLevelList({ lines }: { lines: ServiceLevelLine[] }) {
  if (lines.length === 0) {
    return (
      <Typography.Text type='secondary' className='font-12'>
        —
      </Typography.Text>
    );
  }
  return (
    <div className='freight-markup-rates-grid'>
      {lines.map((line) => (
        <Tooltip key={line.key} title={line.tooltip}>
          <Typography.Text
            className='font-12'
            ellipsis
            style={{ display: 'block', minWidth: 0 }}
          >
            <span className='fw-600'>{line.label}</span>
            <span className='freight-markup-rates-secondary'>
              {' '}
              · {line.markup}
            </span>
          </Typography.Text>
        </Tooltip>
      ))}
    </div>
  );
}

/**
 * Company Settings Markup page — one row per customer with FedEx / UPS
 * service levels listed inline.
 */
const FreightMarkupRates = () => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const mainMenu = useSelector((state: RootState) => state.menu.items);
  const permissions = getPermissions(
    mainMenu,
    'Settings',
    'Freight markup rates'
  );
  const tableScroll = useTableScroll();

  const [rates, setRates] = useState<FreightMarkupRateData[]>([]);
  const [teams, setTeams] = useState<FreightMarkupRateTeamOption[]>([]);
  const [carriers, setCarriers] = useState<FreightMarkupRateCarrierOption[]>(
    []
  );
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<MarkupTeamRow | null>(null);

  const loadPage = useCallback(() => {
    showSpin();
    Promise.all([
      GetFreightMarkupRates(token ?? '', ''),
      GetFreightMarkupRateOptions(token ?? ''),
    ])
      .then(([ratesRes, optionsRes]) => {
        const ratesPayload = ratesRes?.data?.data;
        if (ratesPayload?.is_success && Array.isArray(ratesPayload.data)) {
          setRates(ratesPayload.data);
        } else {
          message.error(
            ratesPayload?.message || 'Failed to fetch markup rates'
          );
        }

        const optionsPayload = optionsRes?.data?.data;
        if (optionsPayload?.is_success) {
          setTeams(optionsPayload.data?.teams || []);
          setCarriers(optionsPayload.data?.carriers || []);
        }
      })
      .catch((error) => {
        console.error('Error loading markup page:', error);
        message.error(
          error?.data?.data?.message ||
            error?.data?.message ||
            'Failed to load markup'
        );
      })
      .finally(() => hideSpin());
  }, [hideSpin, showSpin, token]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const levelsByBucket = useMemo(() => {
    const fedex: FreightMarkupRateCarrierOption[] = [];
    const ups: FreightMarkupRateCarrierOption[] = [];
    for (const carrier of carriers) {
      const bucket =
        courierBucket(carrier.bucket) || courierBucket(carrier.name);
      if (bucket === 'FedEx') fedex.push(carrier);
      if (bucket === 'UPS') ups.push(carrier);
    }
    return { fedex, ups };
  }, [carriers]);

  const rows: TeamMarkupRow[] = useMemo(() => {
    const byTeam = new Map<string, TeamMarkupRow>();

    for (const team of teams) {
      byTeam.set(team.id, {
        team_id: team.id,
        team_name: team.name,
        team_color: team.color || '#000000',
        rates: [],
        fedex: [],
        ups: [],
      });
    }

    const rateLookup = new Map<string, FreightMarkupRateData>();
    for (const rate of rates) {
      let row = byTeam.get(rate.team_id);
      if (!row) {
        row = {
          team_id: rate.team_id,
          team_name: rate.team_name,
          team_color: '#000000',
          rates: [],
          fedex: [],
          ups: [],
        };
        byTeam.set(rate.team_id, row);
      }
      const bucket = courierBucket(rate.courier_name);
      if (bucket !== 'FedEx' && bucket !== 'UPS') {
        continue;
      }
      row.rates.push(rate);
      rateLookup.set(
        `${rate.team_id}::${rate.courier_id}::${rate.service_level}`,
        rate
      );
    }

    const resolvePercent = (
      teamId: string,
      courierId: string,
      serviceLevel: string
    ): number => {
      const exact = rateLookup.get(`${teamId}::${courierId}::${serviceLevel}`);
      if (exact) return Number(exact.markup_percent);
      const all = rateLookup.get(
        `${teamId}::${courierId}::${ALL_SERVICE_LEVELS}`
      );
      if (all) return Number(all.markup_percent);
      return SEED_MARKUP_PERCENT;
    };

    const buildLines = (
      teamId: string,
      carrierList: FreightMarkupRateCarrierOption[]
    ): ServiceLevelLine[] => {
      const lines: ServiceLevelLine[] = [];
      for (const carrier of carrierList) {
        const allRate = rateLookup.get(
          `${teamId}::${carrier.id}::${ALL_SERVICE_LEVELS}`
        );
        const hasLevelOverrides = (carrier.service_levels || []).some((level) =>
          rateLookup.has(
            `${teamId}::${carrier.id}::${level.original_service_level}`
          )
        );
        // Carrier-wide only → one summary line instead of repeating every level.
        if (allRate && !hasLevelOverrides) {
          const percent = Number(allRate.markup_percent);
          const markup = `${percent.toFixed(2)}%`;
          lines.push({
            key: `${carrier.id}::${ALL_SERVICE_LEVELS}`,
            label: 'All service levels',
            markup,
            tooltip: `All service levels: ${markup}`,
          });
          continue;
        }
        for (const level of carrier.service_levels || []) {
          const label = level.display_name || level.original_service_level;
          const percent = resolvePercent(
            teamId,
            carrier.id,
            level.original_service_level
          );
          const markup = `${percent.toFixed(2)}%`;
          lines.push({
            key: `${carrier.id}::${level.original_service_level}`,
            label,
            markup,
            tooltip: `${label}: ${markup}`,
          });
        }
      }
      return lines.sort((a, b) => a.label.localeCompare(b.label));
    };

    const needle = search.trim().toLowerCase();
    return Array.from(byTeam.values())
      .filter((row) =>
        needle ? row.team_name.toLowerCase().includes(needle) : true
      )
      .map((row) => ({
        ...row,
        fedex: buildLines(row.team_id, levelsByBucket.fedex),
        ups: buildLines(row.team_id, levelsByBucket.ups),
      }))
      .sort((a, b) => a.team_name.localeCompare(b.team_name));
  }, [rates, teams, search, levelsByBucket]);

  const columns: ColumnsType<TeamMarkupRow> = [
    {
      title: 'Name',
      dataIndex: 'team_name',
      key: 'team_name',
      width: 200,
      ellipsis: true,
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (_: string, record: TeamMarkupRow) => (
        <Flex align='center' gap='8px' style={{ maxWidth: 184 }}>
          <span
            className='freight-markup-rates-color-dot'
            // eslint-disable-next-line react/forbid-dom-props -- colour comes from the team record
            style={{ backgroundColor: record.team_color || '#000000' }}
          />
          <Typography.Text
            className='font-12 fw-600'
            ellipsis={{ tooltip: record.team_name }}
          >
            {record.team_name}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'FedEx',
      dataIndex: 'fedex',
      key: 'fedex',
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (lines: ServiceLevelLine[]) => <ServiceLevelList lines={lines} />,
    },
    {
      title: 'UPS',
      dataIndex: 'ups',
      key: 'ups',
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (lines: ServiceLevelLine[]) => <ServiceLevelList lines={lines} />,
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (_: unknown, record: TeamMarkupRow) =>
        permissions?.is_update || permissions?.is_create ? (
          <Flex justify='flex-end'>
            <button
              type='button'
              aria-label={`Edit markup for ${record.team_name}`}
              className='c-pointer freight-markup-rates-edit-btn'
              onClick={() => {
                setCurrentTeam({
                  team_id: record.team_id,
                  team_name: record.team_name,
                  team_color: record.team_color,
                  rates: record.rates,
                });
                setDrawerOpen(true);
              }}
            >
              <PencilIcon color='var(--text-primary)' />
            </button>
          </Flex>
        ) : null,
    },
  ];

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          title='Markup'
          showToggle={false}
          onSearchChange={(value: string) => setSearch(value)}
          onSearchClear={() => setSearch('')}
          totalCount={rows.length}
          rangeStart={rows.length === 0 ? 0 : 1}
          rangeEnd={rows.length}
          showCount={true}
          showSearch={true}
          showAddButton={false}
        />
        <Table
          columns={columns}
          dataSource={rows}
          rowKey='team_id'
          pagination={false}
          className='settings-table settings-table-action-cols'
          rowHoverable={false}
          sticky
          scroll={{
            ...(tableScroll || {}),
            y: 'calc(100vh - 240px)',
          }}
          tableLayout='fixed'
        />
      </div>

      {drawerOpen && currentTeam && (
        <CommonDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <FreightMarkupRateForm
            teamRow={currentTeam}
            allRates={rates}
            onClose={() => setDrawerOpen(false)}
            onSaved={() => loadPage()}
          />
        </CommonDrawer>
      )}
    </>
  );
};

export default FreightMarkupRates;
